import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { 
  StudentsListSheet, 
  AnalyticsDialog, 
  QuestionsDialog,
  ClassesOverviewSheet 
} from '../../components/DashboardModals';
import { 
  UserCheck,
  School, 
  ListChecks, 
  AudioWaveform,
  MicVocal,
  Award,
  Clapperboard,
  ChevronRight,
  Sparkles,
  TrendingUp,
  BookOpen,
  Play,
  Zap,
  PenLine,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalClasses: 0,
    totalQuestions: 0,
    moduleStats: []
  });
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [studentsSheetOpen, setStudentsSheetOpen] = useState(false);
  const [classesSheetOpen, setClassesSheetOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [questionsOpen, setQuestionsOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, classesRes, questionsRes, statsRes] = await Promise.all([
        api.get('/users?role=student'),
        api.get('/classes'),
        api.get('/questions'),
        api.get('/statistics?period=monthly')
      ]);
      
      setStats({
        totalStudents: studentsRes.data.length,
        totalClasses: classesRes.data.length,
        totalQuestions: questionsRes.data.length,
        moduleStats: statsRes.data
      });
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (cardType) => {
    switch (cardType) {
      case 'students':
        setStudentsSheetOpen(true);
        break;
      case 'classes':
        setClassesSheetOpen(true);
        break;
      case 'questions':
        setQuestionsOpen(true);
        break;
      default:
        break;
    }
  };

  const handleModuleClick = (moduleType) => {
    switch (moduleType) {
      case 'tts':
        navigate('/teacher/tts');
        break;
      case 'stt':
        navigate('/teacher/stt');
        break;
      case 'mcq':
        navigate('/teacher/mcq');
        break;
      case 'vmcq':
        navigate('/teacher/vmcq');
        break;
      case 'ftb':
        navigate('/teacher/ftb');
        break;
      default:
        break;
    }
  };

  const statCards = [
    { 
      label: 'My Students', 
      value: stats.totalStudents, 
      icon: UserCheck, 
      iconColor: 'text-sky-500',
      bgColor: 'bg-sky-500/10',
      hoverColor: 'hover:border-sky-500/50 hover:shadow-sky-500/20',
      action: 'students',
      actionLabel: 'View Student List'
    },
    { 
      label: 'My Classes', 
      value: stats.totalClasses, 
      icon: School, 
      iconColor: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10',
      hoverColor: 'hover:border-indigo-500/50 hover:shadow-indigo-500/20',
      action: 'classes',
      actionLabel: 'View Classes'
    },
    { 
      label: 'Questions Created', 
      value: stats.totalQuestions, 
      icon: ListChecks, 
      iconColor: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
      hoverColor: 'hover:border-emerald-500/50 hover:shadow-emerald-500/20',
      action: 'questions',
      actionLabel: 'Questions Breakdown'
    },
  ];

  const moduleCards = [
    { 
      name: 'Text to Speech', 
      type: 'tts',
      icon: AudioWaveform, 
      iconColor: 'text-cyan-400',
      bgColor: 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20',
      hoverColor: 'hover:border-cyan-500/50',
      gradientFrom: 'from-cyan-500/20',
      gradientTo: 'to-blue-500/20',
      description: 'Students read and practice pronunciation'
    },
    { 
      name: 'Speech to Text', 
      type: 'stt',
      icon: MicVocal, 
      iconColor: 'text-emerald-400',
      bgColor: 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20',
      hoverColor: 'hover:border-emerald-500/50',
      gradientFrom: 'from-emerald-500/20',
      gradientTo: 'to-teal-500/20',
      description: 'Students listen and type what they hear'
    },
    { 
      name: 'MCQ Quiz', 
      type: 'mcq',
      icon: BookOpen, 
      iconColor: 'text-amber-400',
      bgColor: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
      hoverColor: 'hover:border-amber-500/50',
      gradientFrom: 'from-amber-500/20',
      gradientTo: 'to-orange-500/20',
      description: 'Multiple choice assessments'
    },
    { 
      name: 'Fill the Blanks', 
      type: 'ftb',
      icon: PenLine, 
      iconColor: 'text-orange-400',
      bgColor: 'bg-gradient-to-br from-orange-500/20 to-red-500/20',
      hoverColor: 'hover:border-orange-500/50',
      gradientFrom: 'from-orange-500/20',
      gradientTo: 'to-red-500/20',
      description: 'Fill in missing words exercises',
      isNew: true
    },
    { 
      name: 'Cinematic MCQ', 
      type: 'vmcq',
      icon: Clapperboard, 
      iconColor: 'text-purple-400',
      bgColor: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20',
      hoverColor: 'hover:border-purple-500/50',
      gradientFrom: 'from-purple-500/20',
      gradientTo: 'to-pink-500/20',
      description: 'Video-based MCQ assessments'
    },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">
              Welcome, {user?.name}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage your classes and lab modules
            </p>
          </div>
          {/* Quick Action */}
          <button 
            onClick={() => setAnalyticsOpen(true)}
            className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/20 hover:border-violet-500/40 transition-all"
          >
            <Sparkles className="h-4 w-4 text-violet-400" />
            <span className="text-sm text-violet-300">View Analytics</span>
          </button>
        </div>

        {/* Stats Grid - Interactive Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger-children">
          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={idx} 
                className={`stat-card cursor-pointer transition-all duration-300 border-2 border-transparent ${stat.hoverColor} hover:shadow-lg group`}
                onClick={() => handleCardClick(stat.action)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${stat.bgColor} transition-transform group-hover:scale-110`}>
                        <Icon className={`h-6 w-6 ${stat.iconColor}`} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 dark:text-slate-600 group-hover:text-violet-500 transition-colors" />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-violet-500 dark:text-violet-400 font-medium flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {stat.actionLabel}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Lab Modules */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Lab Modules
            </h2>
            <button 
              onClick={() => setAnalyticsOpen(true)}
              className="text-sm text-violet-500 hover:text-violet-400 font-medium flex items-center gap-1"
            >
              View Stats <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {moduleCards.map((module, idx) => {
                const Icon = module.icon;
                const moduleStats = stats.moduleStats.find(s => s.module_type === module.type);
                return (
                  <Card 
                    key={idx} 
                    className={`module-card cursor-pointer transition-all duration-300 border-2 border-transparent ${module.hoverColor} hover:shadow-lg group overflow-hidden relative`}
                    onClick={() => handleModuleClick(module.type)}
                  >
                    {/* NEW Badge */}
                    {module.isNew && (
                      <div className="absolute top-3 right-3 z-20">
                        <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full animate-pulse flex items-center gap-1">
                          <Star className="h-3 w-3" /> NEW
                        </span>
                      </div>
                    )}
                    {/* Gradient Background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${module.gradientFrom} ${module.gradientTo} opacity-0 group-hover:opacity-100 transition-opacity`} />
                    
                    <CardContent className="p-6 relative">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`inline-flex p-3 rounded-xl ${module.bgColor} transition-transform group-hover:scale-110`}>
                          <Icon className={`h-6 w-6 ${module.iconColor}`} />
                        </div>
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 text-xs text-slate-400 group-hover:bg-violet-500/20 group-hover:text-violet-300 transition-all ${module.isNew ? 'mt-8' : ''}`}>
                          <Play className="h-3 w-3" />
                          Open
                        </div>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                        {module.name}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        {module.description}
                      </p>
                      
                      {/* Stats */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Attempts</span>
                          <span className="font-medium text-slate-900 dark:text-white">
                            {moduleStats?.total_attempts || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Avg Score</span>
                          <span className="font-medium text-slate-900 dark:text-white">
                            {Math.round(moduleStats?.average_score || 0)}%
                          </span>
                        </div>
                        <Progress 
                          value={moduleStats?.average_score || 0} 
                          className="h-1.5" 
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

        {/* Quick Tips + AI Suggestions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-indigo-600" />
                Quick Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {[
                  { color: 'text-cyan-500', text: 'Create TTS questions to help students practice reading aloud' },
                  { color: 'text-emerald-500', text: 'Use STT module to improve listening comprehension' },
                  { color: 'text-amber-500', text: 'Upload Excel files for bulk MCQ questions' },
                  { color: 'text-purple-500', text: 'Add videos for engaging cinematic MCQ tests' },
                ].map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <span className={tip.color}>•</span>
                    {tip.text}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-violet-500/5 to-cyan-500/5 border-violet-500/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" />
                AI Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  {
                    icon: Zap,
                    title: 'Boost Engagement',
                    description: stats.moduleStats.length === 0 
                      ? 'Start by creating questions in any module to track performance.'
                      : 'Students are actively learning! Keep adding varied content.',
                    color: 'text-amber-500'
                  },
                  {
                    icon: TrendingUp,
                    title: 'Performance Tip',
                    description: stats.totalQuestions < 20
                      ? 'Add more questions to provide diverse practice opportunities.'
                      : 'Great question bank! Consider adding more challenging content.',
                    color: 'text-emerald-500'
                  }
                ].map((suggestion, idx) => {
                  const SugIcon = suggestion.icon;
                  return (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-white/50 dark:bg-white/5">
                      <SugIcon className={`h-5 w-5 ${suggestion.color} mt-0.5`} />
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{suggestion.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{suggestion.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals */}
      <StudentsListSheet 
        open={studentsSheetOpen} 
        onOpenChange={setStudentsSheetOpen}
        isTeacher={true}
      />
      <ClassesOverviewSheet
        open={classesSheetOpen}
        onOpenChange={setClassesSheetOpen}
      />
      <AnalyticsDialog 
        open={analyticsOpen} 
        onOpenChange={setAnalyticsOpen}
        title="My Performance Analytics"
      />
      <QuestionsDialog 
        open={questionsOpen} 
        onOpenChange={setQuestionsOpen}
      />
    </DashboardLayout>
  );
};

export default TeacherDashboard;
